import { useEffect, useMemo, useRef, useState } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router";

import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import { Badge } from "@/components/StatusBadge";
import { TenantsLeasesSubNav } from "@/components/TenantsLeasesSubNav";
import { DataTable } from "@/components/table/DataTable";
import { SavedViewChips } from "@/components/table/SavedViewChips";
import type { DataTableColumn, DataTableFilterConfig } from "@/components/table/columns";
import type { InlineEditCommitArgs } from "@/components/table/use-inline-edits";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ApiError, apiFetch } from "@/lib/api";
import type { LeaseResource, Page, TenantResource } from "@/lib/api-types";
import { useIsOwner } from "@/lib/current-actor";
import { TENANT_STATUS_FILL, TENANT_STATUS_LABELS } from "@/lib/enum-labels";
import { labelFor } from "@/lib/field-labels";
import { formatDateCell } from "@/lib/format";
import type { EditCommitOutcome, EditCommitPayload } from "@/lib/inline-edit";
import {
  appendUnknownParams,
  buildListSearch,
  PAGE_PARAM,
  pageSizeFor,
  parseFilterParams,
  parsePageParam,
  parseSortParam,
  SORT_PARAM,
} from "@/lib/list-query";
import { showToast } from "@/lib/toast-queue";
import { useIsPhone } from "@/lib/use-media-query";

import { AddTenantDialog } from "./AddTenantDialog";
import { TenancyReportDialog } from "./TenancyReportDialog";

/** This story's own AD-14 grammar — matches the backend's own
 * `FILTERABLE_FIELDS` (`app/repo/tenants.py`) exactly. */
const FILTERABLE_FIELDS = ["status"] as const;

/** This list's own identity, so a saved view created here never appears on
 * some future list's chip row (mirrors `UsersList.tsx`'s own `USERS_LIST_KEY`). */
const TENANTS_LIST_KEY = "tenants";

/** `TENANT_STATUS_LABELS`'s own keys, in the fixed order the backend's
 * `TENANT_STATUSES` declares them — never a second, independently-typed
 * array of the three values (VC-6.1-12): built from the label map's own
 * keys, not re-listed. */
const STATUS_FILTER_OPTIONS = Object.entries(TENANT_STATUS_LABELS).map(([value, label]) => ({ value, label }));

function patchBodyFor(columnKey: string, payload: EditCommitPayload): Record<string, unknown> {
  if (payload.kind !== "value") {
    throw new Error(`unexpected transition-kind payload for column "${columnKey}"`);
  }
  return { [columnKey]: payload.value === "" ? null : payload.value };
}

/**
 * Story 15.16 — the two columns that PATCH a different entity than their
 * own row (spec Intent). Named here, once, so every place that needs to
 * tell "an ordinary tenant column" from "a lease-date column" (the per-row
 * `edit` function below, `sendPatch`'s own branch) reads the same list
 * rather than re-deriving it.
 */
const LEASE_DATE_COLUMN_KEYS = new Set(["current_lease_start_date", "current_lease_end_date"]);

function isLeaseDateColumnKey(
  columnKey: string,
): columnKey is "current_lease_start_date" | "current_lease_end_date" {
  return LEASE_DATE_COLUMN_KEYS.has(columnKey);
}

/** `current_lease_start_date` -> `start_date`, `current_lease_end_date` ->
 * `end_date` — the real field names on `PATCH /leases/{id}`'s own body
 * (`UpdateLeaseRequest`, Story 15.15), never the Tenant-row column names
 * this list happens to display them under. */
function leaseFieldFor(columnKey: "current_lease_start_date" | "current_lease_end_date"): "start_date" | "end_date" {
  return columnKey === "current_lease_start_date" ? "start_date" : "end_date";
}

//: Story 15.23 — after this list's own cross-entity `PATCH /leases/{current_
//: lease_id}` edit succeeds, the Lease record, Property, and Leases-list
//: query families all get invalidated — alongside, never instead of, this
//: file's own existing `writeLeaseDatesToCache` optimistic write. Mirrors
//: `LeaseRecord.tsx`'s own identically-named, identically-shaped helper
//: (not a cross-file shared abstraction: this story's own judgement call 3
//: says three call sites doesn't justify a new primitive). Three broad
//: top-level prefixes (judgement call 2: broad-and-correct beats narrow-
//: and-risky), never a client-side re-derivation of either bulk resolver's
//: own selection rule (AD-2's own "one place" rule). `invalidateQueries`
//: only marks currently-mounted observers stale and refetches nothing when
//: no observer is mounted, so this adds no cost to a normal page load
//: (VC-15.23-04).
function invalidateLeaseDerivedViews(queryClient: QueryClient): Promise<unknown> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ["leases"] }),
    queryClient.invalidateQueries({ queryKey: ["tenants"] }),
    queryClient.invalidateQueries({ queryKey: ["properties"] }),
  ]);
}

/**
 * `/tenants-leases` (Story 6.1) — mirrors `UsersList.tsx`'s own proven
 * "shared data table with saved views" shape exactly (spec Intent), reusing
 * the existing `/tenants-leases` nav destination (EXPERIENCE.md's IA table)
 * rather than a new route: no `Lease` exists yet (Story 6.3), so this
 * screen is the tenants half only — the same nav slot's own real page,
 * where `DestinationPlaceholder` stood in before this story.
 */
export function TenantsList() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const queryClient = useQueryClient();
  //: `POST /tenants` is `role=Role.OWNER` (Boundaries & Constraints) — the
  //: create affordance is withheld from anyone but a confirmed Owner, not
  //: merely "not Read-only" (a Member is not Read-only and would otherwise
  //: see this button, then 403 the moment they submitted), mirroring
  //: `PropertiesList.tsx`'s identical `isOwner`-gated "Add a property"
  //: button (Tier A fix round, Fix 1).
  const isOwner = useIsOwner();
  const [removeTarget, setRemoveTarget] = useState<TenantResource | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | undefined>(undefined);
  const [createOpen, setCreateOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    document.title = "Tenants — PropDesk";
  }, []);

  const sort = parseSortParam(searchParams.get(SORT_PARAM));
  const activeFilters = parseFilterParams(searchParams, FILTERABLE_FIELDS);
  const page = parsePageParam(searchParams.get(PAGE_PARAM));
  const pageSize = pageSizeFor(isPhone);
  const search = appendUnknownParams(
    buildListSearch({ sort, filters: activeFilters, page, pageSize }),
    searchParams,
    FILTERABLE_FIELDS,
  );
  const listQueryKey = ["tenants", "list", search] as const;

  const listQuery = useQuery({
    queryKey: listQueryKey,
    queryFn: () => apiFetch<Page<TenantResource>>(`/tenants?${search}`),
    placeholderData: keepPreviousData,
  });

  const hasActiveFilters = Object.keys(activeFilters).length > 0;
  const unfilteredTotalQuery = useQuery({
    queryKey: ["tenants", "total"],
    queryFn: () => apiFetch<Page<TenantResource>>("/tenants?page_size=1"),
    enabled: hasActiveFilters,
  });

  const filters = useMemo<DataTableFilterConfig[]>(
    () => [{ field: "status", label: labelFor("status"), options: STATUS_FILTER_OPTIONS }],
    [],
  );

  function writeRowToCache(updated: TenantResource) {
    queryClient.setQueriesData<Page<TenantResource>>({ queryKey: ["tenants"] }, (existing) => {
      if (existing === undefined) {
        return existing;
      }
      return {
        ...existing,
        data: existing.data.map((candidate) => (candidate.id === updated.id ? updated : candidate)),
      };
    });
  }

  /**
   * Story 15.16 — after a successful `PATCH /leases/{current_lease_id}`,
   * every cached tenant row sharing that same `current_lease_id` gets the
   * lease's own freshly-returned dates, not only the row that was actually
   * edited (`writeRowToCache` above matches by tenant id, which would miss
   * a co-tenant on the exact same lease). This is what keeps co-tenants
   * consistent immediately, in this session, without waiting on a real
   * network refetch (Acceptance Criteria's co-tenant case; a genuine page
   * reload is separately correct because the server itself was PATCHed).
   */
  function writeLeaseDatesToCache(leaseId: string, startDate: string, endDate: string | null) {
    queryClient.setQueriesData<Page<TenantResource>>({ queryKey: ["tenants"] }, (existing) => {
      if (existing === undefined) {
        return existing;
      }
      return {
        ...existing,
        data: existing.data.map((candidate) =>
          candidate.current_lease_id === leaseId
            ? { ...candidate, current_lease_start_date: startDate, current_lease_end_date: endDate }
            : candidate,
        ),
      };
    });
  }

  function removeRowFromCache(id: string) {
    queryClient.setQueriesData<Page<TenantResource>>({ queryKey: ["tenants"] }, (existing) => {
      if (existing === undefined || !existing.data.some((candidate) => candidate.id === id)) {
        return existing;
      }
      return {
        ...existing,
        data: existing.data.filter((candidate) => candidate.id !== id),
        meta: { ...existing.meta, total: Math.max(0, existing.meta.total - 1) },
      };
    });
  }

  /**
   * Deleting a tenant (Boundaries & Constraints: "a delete is soft with the
   * 30-day restore affordance"). Runs the real, already-audited `DELETE
   * /tenants/{id}` — this dialog adds a UI trigger and a confirmation,
   * nothing more, mirroring `UsersList.tsx`'s identical "Remove" flow.
   */
  async function confirmRemove() {
    if (removeTarget === null) {
      return;
    }
    setIsRemoving(true);
    setRemoveError(undefined);
    try {
      await apiFetch<{ deleted: boolean }>(`/tenants/${removeTarget.id}`, { method: "DELETE" });
      removeRowFromCache(removeTarget.id);
      setRemoveTarget(null);
    } catch (error) {
      setRemoveError(
        error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsRemoving(false);
    }
  }

  /**
   * `PATCH /tenants/{id}` carries no optimistic-concurrency token (unlike
   * `PATCH /users/{id}`'s own `expected_updated_at`) — mirrors `PropertyRecord.
   * tsx`'s identical, simpler `sendPatch`/`commitEdit` shape rather than
   * `UsersList.tsx`'s fuller conflict-resolution one: no `onResolveConflict`
   * is wired here because the backend has no `conflict` outcome to resolve.
   *
   * Story 15.16, spec judgement call 2: `current_lease_start_date`/
   * `current_lease_end_date` are the one exception — they PATCH
   * `/leases/{row.current_lease_id}`, never `/tenants/{row.id}`, because
   * they display a *Lease's* own dates (Story 15.12), not the tenant's.
   * That branch lives here, in this list's own `sendPatch`, rather than in
   * the shared `DataTable`/`use-inline-edits` machinery — `use-inline-
   * edits.ts`'s own doc comment states it deliberately "never knows which
   * endpoint it is talking to," and this one table's one special case is
   * not a reason to break that for every other list in the app.
   */
  async function sendPatch(
    row: TenantResource,
    column: DataTableColumn<TenantResource>,
    payload: EditCommitPayload,
  ): Promise<EditCommitOutcome> {
    if (isLeaseDateColumnKey(column.key)) {
      return sendLeaseDatePatch(row, column.key, payload);
    }
    try {
      const updated = await apiFetch<TenantResource>(`/tenants/${row.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patchBodyFor(column.key, payload)),
      });
      writeRowToCache(updated);
      return { ok: true };
    } catch (error) {
      if (error instanceof ApiError) {
        return { ok: false, message: error.message };
      }
      throw error;
    }
  }

  /**
   * The cross-entity half of `sendPatch` above. `row.current_lease_id` is
   * guaranteed non-null in practice — the column's own per-row `edit`
   * function below withholds the edit affordance entirely for a row with no
   * current lease (VC-15.16-03), so `DataTable` never lets a commit reach
   * here for one — but this still refuses honestly rather than firing a
   * PATCH against `/leases/null` if that invariant is ever violated.
   *
   * The same `end_date >= start_date` validation Story 15.15 built lives on
   * `PATCH /leases/{id}` itself (`app/api/leases.py::update_lease`) — this
   * function does not duplicate it, it only has to let a `422` surface as
   * an honest `EditCommitOutcome` the same way every other failed edit in
   * this table already does (VC-15.16-04).
   */
  async function sendLeaseDatePatch(
    row: TenantResource,
    columnKey: "current_lease_start_date" | "current_lease_end_date",
    payload: EditCommitPayload,
  ): Promise<EditCommitOutcome> {
    if (payload.kind !== "value") {
      throw new Error(`unexpected transition-kind payload for column "${columnKey}"`);
    }
    if (row.current_lease_id === null || row.current_lease_id === undefined) {
      return { ok: false, message: "This tenant has no current lease to edit." };
    }
    const field = leaseFieldFor(columnKey);
    try {
      const updatedLease = await apiFetch<LeaseResource>(`/leases/${row.current_lease_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [field]: payload.value === "" ? null : payload.value }),
      });
      // Live-caught: this PATCHes the shared *lease*, not the tenant row
      // being edited, so every co-tenant on the same lease is silently
      // rewritten in place too — visually identical to an ordinary
      // single-field edit. Name them here rather than leave that side
      // effect unannounced (the co-tenant's own row still gets no signal,
      // but the person making the edit does).
      const coTenantNames = (listQuery.data?.data ?? [])
        .filter((tenant) => tenant.id !== row.id && tenant.current_lease_id === row.current_lease_id)
        .map((tenant) => tenant.full_name);
      writeLeaseDatesToCache(row.current_lease_id, updatedLease.start_date, updatedLease.end_date);
      await invalidateLeaseDerivedViews(queryClient);
      if (coTenantNames.length > 0) {
        showToast(`Also updated the same lease for ${coTenantNames.join(", ")}.`);
      }
      return { ok: true };
    } catch (error) {
      if (error instanceof ApiError) {
        return { ok: false, message: error.message };
      }
      throw error;
    }
  }

  async function commitEdit(args: InlineEditCommitArgs<TenantResource>): Promise<EditCommitOutcome> {
    return sendPatch(args.row, args.column, args.payload);
  }

  const columns = useMemo<DataTableColumn<TenantResource>[]>(
    () => [
      { key: "full_name", edit: { kind: "text" } },
      { key: "mobile_phone", edit: { kind: "text" } },
      {
        // Ordered ahead of "email" (rather than the AC's literal field
        // order) so it survives `DataTable`'s phone-row `columns.slice(1,3)`
        // secondary-column rule (`columns.ts`'s own doc comment: phone
        // selection is driven by column order, not a second flag) — status
        // is what a phone glance actually needs; email is empty for every
        // seeded tenant today and was winning that slot by accident.
        key: "status",
        render: (row) => (
          <Badge label={TENANT_STATUS_LABELS[row.status] ?? row.status} fill={TENANT_STATUS_FILL[row.status] ?? "neutral"} />
        ),
        edit: { kind: "select", options: STATUS_FILTER_OPTIONS },
      },
      { key: "email", edit: { kind: "text" } },
      {
        // Story 15.26 — the previously-missing "Property" column, showing
        // the property behind the tenant's *current* lease: the exact same
        // lease already resolved (`current_lease_id`) for the two lease-
        // date columns immediately below, never a second, independently-
        // chosen one (spec Intent). Positioned right before those two
        // columns — property context naturally precedes the lease dates it
        // explains (spec judgement call 3).
        //
        // `sortable: false` for the identical reason `current_lease_start_
        // date`/`current_lease_end_date` below are: a bulk-resolved,
        // computed value with no real, sortable backend column
        // (`app.repo.tenants.SORTABLE_FIELDS` has no such entry).
        //
        // Mirrors `LeasesList.tsx`'s own "Tenants" column (Story 15.21) and
        // `PropertiesList.tsx`'s own "Tenant" column (Story 15.24): a real,
        // separately-clickable `<Link>` to the property's own record page,
        // with `event.stopPropagation()` so clicking it does not also fire
        // this row's own `getRowHref` navigation to the tenant record.
        //
        // No current lease (the same condition that already leaves "Lease
        // start"/"Lease end" blank for this row) renders an honest em-dash
        // — never a crash, never a fabricated property name.
        key: "current_property_nickname",
        sortable: false,
        render: (row) =>
          row.current_prop_id && row.current_property_nickname ? (
            <Link
              to={`/properties/${row.current_prop_id}`}
              className="text-ink underline"
              onClick={(event) => event.stopPropagation()}
            >
              {row.current_property_nickname}
            </Link>
          ) : (
            "—"
          ),
      },
      {
        // Not a real, sortable `Tenant` column the way `move_in_date` was
        // (`app.repo.tenants.SORTABLE_FIELDS`) — a bulk-resolved, computed
        // value (spec: out of scope to build new sort machinery for this
        // story). `sortable: false` so the header is never a clickable
        // affordance that would 400 against `GET /tenants?sort=...` the
        // moment someone clicked it.
        key: "current_lease_start_date",
        tabularFigures: true,
        sortable: false,
        render: (row) => (row.current_lease_start_date ? formatDateCell(row.current_lease_start_date) : "—"),
        // Story 15.16 — per-row editability (`columns.ts`'s own
        // `DataTableColumn.edit` doc comment): a real `text`-kind edit
        // config for a tenant with a current lease, `undefined` (no edit
        // affordance at all, VC-15.16-03) for one without. `row.
        // current_lease_id` — never `row.current_lease_start_date` itself —
        // is the right null-check: a lease with a real start date but no
        // resolvable id would be a backend defect, not a legitimate "no
        // lease" state, and this column must not paper over that by
        // silently withholding edit anyway.
        edit: (row) => (row.current_lease_id ? { kind: "text" } : undefined),
      },
      {
        key: "current_lease_end_date",
        tabularFigures: true,
        sortable: false,
        render: (row) => (row.current_lease_end_date ? formatDateCell(row.current_lease_end_date) : "—"),
        edit: (row) => (row.current_lease_id ? { kind: "text" } : undefined),
      },
    ],
    [],
  );

  const errorMessage = listQuery.error instanceof ApiError ? listQuery.error.message : undefined;

  return (
    <div className="mx-auto max-w-content-max">
      <ReadOnlyNotice className="mb-4" />
      <TenantsLeasesSubNav />
      <div className="mb-2 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 ref={headingRef} tabIndex={-1} className="text-title font-semibold tracking-title text-ink md:hidden">
            Tenants
          </h1>
          <p className="mt-2 text-body text-ink-secondary">
            Every tenant profile — the same record across every lease renewal.
          </p>
        </div>
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          {isOwner ? (
            <Button type="button" variant="ghost" onClick={() => setExportOpen(true)} className="w-full md:w-auto">
              Export CSV
            </Button>
          ) : null}
          {isOwner ? (
            <Button type="button" onClick={() => setCreateOpen(true)} className="w-full md:w-auto">
              Add tenant
            </Button>
          ) : null}
        </div>
      </div>
      <SavedViewChips listKey={TENANTS_LIST_KEY} />
      <DataTable
        caption="Tenants"
        itemNamePlural="tenants"
        tint="violet"
        columns={columns}
        page={listQuery.data}
        isPending={listQuery.isPending}
        isFetching={listQuery.isFetching}
        isError={listQuery.isError}
        {...(errorMessage ? { errorMessage } : {})}
        filters={filters}
        {...(unfilteredTotalQuery.data ? { unfilteredTotal: unfilteredTotalQuery.data.meta.total } : {})}
        getRowHref={(row) => `/tenants-leases/${row.id}`}
        emptyState={
          isOwner
            ? {
                heading: "No tenants yet.",
                body: "Add a tenant to start building their record — it will carry every lease renewal that follows.",
                action: { label: "Add tenant", onClick: () => setCreateOpen(true) },
              }
            : {
                heading: "No tenants yet.",
                body: "Tenants will appear here once an Owner adds one.",
              }
        }
        onCommitEdit={commitEdit}
        {...(isOwner
          ? {
              rowActions: (row: TenantResource) => (
                <Button
                  type="button"
                  variant="ghost"
                  aria-label={`Remove ${row.full_name}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    setRemoveTarget(row);
                  }}
                >
                  Remove
                </Button>
              ),
            }
          : {})}
      />
      {isOwner ? (
        <AddTenantDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={(id) => navigate(`/tenants-leases/${id}`)}
        />
      ) : null}
      {isOwner ? <TenancyReportDialog open={exportOpen} onOpenChange={setExportOpen} /> : null}
      {removeTarget ? (
        <ConfirmDialog
          open={removeTarget !== null}
          onOpenChange={(open) => {
            if (!open) {
              setRemoveTarget(null);
              setRemoveError(undefined);
            }
          }}
          title="Remove this tenant?"
          description={`Removing ${removeTarget.full_name} takes their record out of the active list right away. It can be restored for 30 days.`}
          confirmLabel={`Remove ${removeTarget.full_name}`}
          confirmingLabel="Removing…"
          isConfirming={isRemoving}
          onConfirm={() => void confirmRemove()}
          fallbackFocusRef={headingRef}
          {...(removeError ? { errorMessage: removeError } : {})}
        />
      ) : null}
    </div>
  );
}
